import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Poster-size massive number stat.
 *
 * Apple Health vibe — number reads at 64-96px display weight, almost
 * billboard. Use as the centerpiece of a section, not in a 4-up grid
 * (that's <Stat>).
 */
export type BigStatSize = "md" | "lg" | "xl";

// DEM-40: shrunk from poster-size to operational-size so dashboards
// lead with the table, not the hero number. md/lg/xl preserved for callers.
const VALUE_SIZES: Record<BigStatSize, string> = {
  md: "text-2xl sm:text-3xl",
  lg: "text-3xl sm:text-4xl",
  xl: "text-4xl sm:text-5xl",
};

export type BigStatProps = {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  size?: BigStatSize;
  /** Optional supporting node rendered beside the number */
  aside?: ReactNode;
  className?: string;
  /** Override default LTR — Hebrew labels read RTL but numbers stay LTR */
  numberDir?: "ltr" | "rtl";
};

export function BigStat({
  label,
  value,
  hint,
  size = "lg",
  aside,
  className,
  numberDir = "ltr",
}: BigStatProps) {
  return (
    <div className={cn("min-w-0", className)}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] opacity-65">
        {label}
      </p>
      <div className="mt-3 flex items-end gap-4">
        <p
          className={cn(
            VALUE_SIZES[size],
            "font-bold tracking-[-0.04em] leading-[0.85] tabular-nums",
          )}
          dir={numberDir}
        >
          {value}
        </p>
        {aside ? <div className="pb-1 text-sm opacity-75">{aside}</div> : null}
      </div>
      {hint ? (
        <p className="mt-3 text-sm opacity-70 leading-snug">{hint}</p>
      ) : null}
    </div>
  );
}
